import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { apiJson } from '@/lib/apiClient';
import { commissioningQueryKey } from '@/features/commissioning/useCommissioning';
import { adminLiveQueryKey } from '@/features/live/useAdminLive';
import { tenantsQueryKey } from '@/features/tenants/useTenants';
import { adminVehiclesQueryKey } from '@/features/vehicles/useAdminVehicles';

/**
 * Provisioning (slice 18).
 *
 * Types mirror the wire contract in `api/src/modules/admin/routes.ts` (seam S1)
 * and are never re-declared from the screen's point of view.
 */

export interface ProvisionBody {
  /**
   * 🔴 A `devices` ROW UUID, NOT AN IMEI (slice 18d). The tracker must already
   * be in the register: provisioning installs hardware we hold, and the server
   * reads the IMEI off that row. A typed-in IMEI could create a Traccar device
   * for a unit nobody owns, with no record saying so — which is why the field
   * changed shape rather than gaining a lookup.
   */
  deviceId: string;
  plate: string;
  label?: string | null;
  protocol: string;
  model?: string | null;
  vehicleType?: string | null;
  trackerModel?: string | null;
  reportPeriodSec?: number | null;
  /**
   * 🔴 `simMsisdn` / `simCarrier` ARE GONE, DELIBERATELY (slice 18d). A SIM is
   * not a property of a vehicle: it is a row in the `sims` register that reaches
   * the vehicle through its device. The body schema is `.strict()`, so sending
   * either is now a 400 — and accepting a typed SIM string here is exactly the
   * two-representations defect this slice exists to remove.
   */
  /**
   * gt06 only. Sent once and never read back — there is no GET that returns it.
   * A credential that can be fetched is a credential that will end up in a log.
   */
  commandPassword?: string | null;
  plan?: 'track' | 'fleet' | 'pro';
  /**
   * When the tracker was PHYSICALLY fitted (slice 18f). Absent means now.
   *
   * ISO-8601 WITH AN OFFSET — the server's zod is `datetime({ offset: true })`.
   * A bare local timestamp is an instant with no zone, and this value becomes
   * both the assignment's `from_at` and the `trip_coverage` watermark. An hour
   * of ambiguity there is an hour of a customer's history nothing will detect.
   */
  at?: string;
}

export interface ProvisionResponse {
  vehicleId: string;
  traccarDeviceId: number;
  /** Always null. Provisioning does not make a vehicle visible to its tenant. */
  liveAt: null;
}

/** 18g adds `tracker_fitted` — the check that FAILS, rather than a 404, when a
 *  vehicle has no tracker. See `verifyChecks.ts` for why it is a check. */
export type CheckId =
  | 'tracker_fitted'
  // Slice 30 — the go-live gate's vehicle-level check that an install record has
  // been confirmed live. It arrives in the full `runVerifyChecks` report, so
  // `CheckRow` must be able to label it (`provision.checks.install_confirmed`).
  | 'install_confirmed'
  | 'device_registered'
  | 'position_received'
  | 'position_plausible';

export interface CheckResult {
  id: CheckId;
  passed: boolean;
  detail: string;
}

export interface VerifyResponse {
  vehicleId: string;
  /** 18g — NULL when no tracker is fitted. The report still exists; its
   *  `tracker_fitted` check is the failed one. */
  traccarDeviceId: number | null;
  checks: CheckResult[];
  allPassed: boolean;
}

export interface GoLiveResponse {
  vehicleId: string;
  liveAt: string;
  /**
   * 18g — TRUE when this call UN-HID a vehicle that had been withdrawn, rather
   * than clearing one for the first time. Two different events for the operator
   * ("the customer can see it AGAIN" vs "the customer can see it"), and `liveAt`
   * cannot tell them apart because it is deliberately never rewritten.
   */
  wasHidden: boolean;
}

/**
 * 18g — un-go-live. NOTHING is required.
 *
 * 🔴 `reason` HERE IS FREE TEXT FOR THE AUDIT ROW AND DRIVES NOTHING. It is NOT
 * the `RemovalReason` enum that decides customer visibility on the UN-ASSIGN
 * call (`useUnassignVehicleDevice`). Two different things must not share a name
 * that suggests they are one — the server says the same in `UnGoLiveBodyZ`.
 */
export interface UnGoLiveBody {
  at?: string;
  reason?: string;
}

export interface UnGoLiveResponse {
  vehicleId: string;
  hiddenAt: string;
  /**
   * FALSE when it was ALREADY hidden — a 200, not a 409, and `hiddenAt` is the
   * ORIGINAL instant. The surface must say "already hidden", never an error: a
   * second click is not a fault, and re-stamping would rewrite when the customer
   * stopped seeing the vehicle.
   */
  changed: boolean;
}

export function useProvisionVehicle(
  tenantId: string,
): UseMutationResult<ProvisionResponse, Error, ProvisionBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProvisionBody) =>
      apiJson<ProvisionResponse>(`/api/admin/tenants/${tenantId}/vehicles`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // The tenant's vehicle count does NOT change yet — the row is not live. The
    // lists are invalidated anyway because a later go-live will change them, and
    // a stale count is the kind of small lie that erodes trust in the screen.
    //
    // 🔴 `commissioningQueryKey` TOO (18f): assignment moves the tracker OFF the
    // bench. Leaving that list alone would show a device as still awaiting a
    // customer seconds after it was given one — two screens, one fact,
    // disagreeing.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
      void qc.invalidateQueries({ queryKey: adminVehiclesQueryKey });
      void qc.invalidateQueries({ queryKey: commissioningQueryKey });
    },
  });
}

export function useRunChecks(): UseMutationResult<VerifyResponse, Error, string> {
  return useMutation({
    mutationFn: (vehicleId: string) =>
      apiJson<VerifyResponse>(`/api/admin/vehicles/${vehicleId}/checks`),
  });
}

export function useGoLive(): UseMutationResult<GoLiveResponse, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vehicleId: string) =>
      apiJson<GoLiveResponse>(`/api/admin/vehicles/${vehicleId}/go-live`, { method: 'POST' }),
    // Going live changes what the customer can see, so EVERY staff surface that
    // states it has to be refetched: the tenant's active-vehicle count, the
    // global vehicle list's "awaiting go-live" figure, and the `live: false`
    // ring on the global map.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
      void qc.invalidateQueries({ queryKey: adminVehiclesQueryKey });
      void qc.invalidateQueries({ queryKey: adminLiveQueryKey });
    },
  });
}

/**
 * 18g — WITHDRAW a vehicle from the customer's view. The exact inverse of
 * `useGoLive`, and it invalidates the SAME three lists for the same reason: what
 * a customer can see just changed, so every staff surface that states it has to
 * be refetched or two screens disagree about one fact.
 *
 * 🔴 IT IS IDEMPOTENT AND `changed: false` IS NOT AN ERROR. The server answers
 * 200 with the ORIGINAL `hiddenAt` when the vehicle was already hidden; the panel
 * says "already hidden" rather than showing a failure.
 */
export function useUnGoLive(): UseMutationResult<
  UnGoLiveResponse,
  Error,
  { vehicleId: string; body?: UnGoLiveBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, body }) =>
      apiJson<UnGoLiveResponse>(`/api/admin/vehicles/${vehicleId}/un-go-live`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
      void qc.invalidateQueries({ queryKey: adminVehiclesQueryKey });
      void qc.invalidateQueries({ queryKey: adminLiveQueryKey });
    },
  });
}

// ── Tenant provisioning (slice 18b) ───────────────────────────────────────────

export interface ProvisionTenantBody {
  name: string;
  /** Required, with no default — see NewTenantDialog's header. */
  plan: 'track' | 'fleet' | 'pro';
  /** Integer cents, or null for "no rate recorded". NEVER 0 to mean absent. */
  monthlyRateCents?: number | null;
  timezone?: string | undefined;
  language?: 'en' | 'so';
  owner: { phone: string; name: string; password: string };
}

export interface ProvisionTenantResponse {
  tenantId: string;
  traccarGroupId: number;
  ownerUserId: string;
}

export function useProvisionTenant(): UseMutationResult<
  ProvisionTenantResponse,
  Error,
  ProvisionTenantBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProvisionTenantBody) =>
      apiJson<ProvisionTenantResponse>('/api/admin/tenants', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: tenantsQueryKey }),
  });
}
